import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from "react";

export type SourcesDefaultState = "collapsed" | "titles" | "expanded";

// Nur die Werte in Anzeige-Reihenfolge. Beschriftung + Hinweis stehen im
// Katalog (`settings.quellen[wert]`).
export const SOURCES_DEFAULT_OPTIONS: { value: SourcesDefaultState }[] = [
  { value: "collapsed" },
  { value: "titles" },
  { value: "expanded" },
];

const STORAGE_KEY = "sb_sources_default_state";
const DEFAULT_VALUE: SourcesDefaultState = "titles";

interface Ctx {
  state: SourcesDefaultState;
  setState: (s: SourcesDefaultState) => void;
}

const SourcesPrefCtx = createContext<Ctx | null>(null);

function isValid(v: unknown): v is SourcesDefaultState {
  return v === "collapsed" || v === "titles" || v === "expanded";
}

function getInitial(): SourcesDefaultState {
  const saved = typeof localStorage !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
  return isValid(saved) ? saved : DEFAULT_VALUE;
}

export function SourcesPrefProvider({ children }: { children: ReactNode }) {
  const [state, setStateRaw] = useState<SourcesDefaultState>(getInitial);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, state);
  }, [state]);

  const setState = useCallback((s: SourcesDefaultState) => setStateRaw(s), []);

  return <SourcesPrefCtx.Provider value={{ state, setState }}>{children}</SourcesPrefCtx.Provider>;
}

export function useSourcesPref(): Ctx {
  const v = useContext(SourcesPrefCtx);
  if (!v) throw new Error("useSourcesPref outside SourcesPrefProvider");
  return v;
}
